/**
 * Transcript folding — persisted/live frames into what the transcript shows.
 *
 * `display_json` is raw JSON: sessions from before the redesign carry
 * retired frame types (`canvas.*`, `knowledge.proposal`), and the contract
 * says unknown types are skipped, not fatal (DECISIONS #62). The fold reads
 * every frame structurally and ignores what it does not know, so an old
 * session still replays its prose, its tool chips, and its usage footer.
 *
 *  - `buildTurnBlocks`: one assistant turn's frames -> ordered blocks (prose
 *    runs, activity chips, diagrams, usage footer).
 *  - `diagramsFrom*`: the session's diagrams, in order, de-duplicated.
 *
 * Round 4 added `parts` to a diagram: the named nodes and edges the reader can
 * click for a plain sentence, a shape and an example. They are narrowed here
 * with everything else, and an absent `parts` folds to an empty list.
 */

import type { ChatMessage, DiagramKind, Usage } from '../../api/types';
import { frameData, frameType } from '../../lib/frames';

/** A frame as the folds accept it: typed live frame or raw persisted object. */
export type FrameLike = unknown;

/**
 * One clickable piece of a diagram (round 4, owner: "Don't you think those
 * nodes and edges should be clickable in the diagram? Like show some stuff
 * when they are clicked? Maybe message schemas?").
 *
 * `label` matches a node or edge label in the mermaid source VERBATIM: that
 * string is how the client finds the drawn element to make interactive, so it
 * is a join key, not copy.
 *
 * Declared here rather than mirrored: api/types.ts is the contract mirror's
 * file, and the transcript must not wait on it. Every field is read
 * structurally below, so a server that has not shipped `parts` yet folds to an
 * empty list and every surface behaves exactly as it did before.
 */
export interface DiagramPart {
  label: string;
  kind: 'node' | 'edge';
  /** one plain sentence: what this piece of the picture is */
  what: string;
  /** where it runs, when the server knows; null when it does not */
  runs_on: string | null;
  /** the message/record shape it carries, as a plain object */
  shape: Record<string, unknown> | null;
  /** one concrete example of that shape (any JSON value; null when absent) */
  example: unknown;
  /**
   * How the claim is known (north-star round, DECISIONS #76): observed (a
   * live sample), verified (read from the code this turn), inferred (the
   * guide's reading). Null when the model did not say, which is every part
   * drawn before the amendment.
   */
  source: 'observed' | 'verified' | 'inferred' | null;
  /** path:line receipts behind the claim, verbatim from tool results */
  evidence: string[];
  /** the part's mark on a what-changed picture (DECISIONS #78) */
  change: 'new' | 'changed' | 'removed' | null;
  /** the runtime entity ids it runs on or touches (2026-09-07), [] when none */
  runtime: string[];
}

/**
 * The redraw moved something the reader did NOT ask about (round 8).
 *
 * A region ask sends the drawing back with the parts to open up or fold
 * (api/sse.ts `DiagramRegionFocus`); the server measures the redraw against
 * that source and puts `region_drift` on the next `diagram` frame when the
 * rest of the picture did not hold still. It is a diff over MERMAID LINES
 * (`removed` is what the old source had and this one does not; `added` is the
 * reverse). Carried for the record: the pane stopped printing it on
 * 2026-09-06 (owner: "There's no need for the What changed here").
 *
 * Narrowed structurally, like `parts` above and for the same reason: a frame
 * without the key (which is every frame drawn before this round, and every
 * redraw that complied) must fold exactly as it always has.
 */
export interface RegionDrift {
  /** lines the previous source had that the redraw dropped or rewrote */
  removed: string[];
  /** lines the redraw introduced outside the region */
  added: string[];
}

/**
 * A diagram as the app holds it: the wire's DiagramData plus its parts, always
 * an array (empty = nothing in the picture is clickable, which is exactly how
 * every diagram behaved before round 4). Structurally identical to
 * `DiagramData` in its other fields, so it passes anywhere that type is asked
 * for; when the mirror lands `parts` this can collapse into it.
 */

export interface SessionDiagram {
  /** server-minted UUID v4 */
  diagram_id: string;
  title: string;
  kind: DiagramKind;
  mermaid: string;
  parts: DiagramPart[];
  /** null on every drawing that was not a region redraw (which is most) */
  drift?: RegionDrift | null;
}

/**
 * `thinking` is the model's own summary of how it is working the answer out
 * (round 5). It is NOT answer prose and never becomes any: it renders inside
 * the turn's activity run, and every path that turns a turn into text for a
 * human to keep (copy as markdown, the export) matches on `prose` and
 * `diagram` and leaves this kind on the floor.
 */
export type TurnBlock =
  | { kind: 'prose'; text: string }
  | { kind: 'thinking'; text: string }
  | { kind: 'activity'; toolId: string; tool: string; summary: string | null }
  | { kind: 'diagram'; data: SessionDiagram }
  | { kind: 'usage'; usage: Usage; stopReason: string }
  | { kind: 'error'; message: string; retryable: boolean };

const DIAGRAM_KINDS: readonly DiagramKind[] = [
  'flowchart',
  'sequence',
  'er',
  'state',
  'architecture',
];

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/**
 * Narrow a diagram's `parts`. A part needs a label to find its element and a
 * sentence to say when it is found; anything else it carries is a bonus, and
 * anything malformed is dropped rather than fatal. An unrecognized `kind`
 * reads as a node: the kind only tints a badge, and losing the whole part over
 * it would cost the reader the explanation.
 */
export function asDiagramParts(value: unknown): DiagramPart[] {
  if (!Array.isArray(value)) return [];
  const parts: DiagramPart[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    const record = asRecord(entry);
    if (record === null) continue;
    const label = asString(record['label'])?.trim() ?? '';
    const what = asString(record['what'])?.trim() ?? '';
    if (label.length === 0 || what.length === 0) continue;
    // One panel per label: the label is the join key to the drawing, so two
    // parts claiming it would fight over the same element.
    if (seen.has(label)) continue;
    seen.add(label);
    const source = asString(record['source']);
    const change = asString(record['change']);
    parts.push({
      label,
      kind: asString(record['kind']) === 'edge' ? 'edge' : 'node',
      what,
      runs_on: asString(record['runs_on']),
      shape: asRecord(record['shape']),
      example: record['example'] ?? null,
      source:
        source === 'observed' || source === 'verified' || source === 'inferred'
          ? source
          : null,
      evidence: asLines(record['evidence']),
      change:
        change === 'new' || change === 'changed' || change === 'removed'
          ? change
          : null,
      runtime: asLines(record['runtime']),
    });
  }
  return parts;
}

/** The non-blank strings of an array, in order; empty for anything else. */
function asLines(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const lines: string[] = [];
  for (const entry of value) {
    const line = asString(entry)?.trim() ?? '';
    if (line.length > 0) lines.push(line);
  }
  return lines;
}

/**
 * Narrow a `diagram` frame's drift note. Null means "say nothing", which is
 * what every ordinary diagram gets, what a redraw that held the rest of the
 * picture still gets, and what a shape we do not recognise gets.
 *
 * A recognised note with no lines in it still counts as drift: the server only
 * attaches one when a redraw drifted, so the frame saying so is the fact, and
 * the lines are the detail it may or may not be able to give.
 */
export function asRegionDrift(value: unknown): RegionDrift | null {
  const record = asRecord(value);
  if (record === null) return null;
  return { removed: asLines(record['removed']), added: asLines(record['added']) };
}

/** Narrow a `diagram` frame's data; null when malformed (skip silently). */
export function asDiagramData(data: Record<string, unknown> | null): SessionDiagram | null {
  if (data === null) return null;
  const diagramId = asString(data['diagram_id']);
  const title = asString(data['title']);
  const kind = asString(data['kind']);
  const mermaid = asString(data['mermaid']);
  if (diagramId === null || title === null || mermaid === null) return null;
  if (kind === null || !(DIAGRAM_KINDS as readonly string[]).includes(kind)) return null;
  return {
    diagram_id: diagramId,
    title,
    kind: kind as DiagramKind,
    mermaid,
    parts: asDiagramParts(data['parts']),
    drift: asRegionDrift(data['region_drift']),
  };
}


/**
 * Walk one turn's frames into display blocks. Text deltas extend the
 * current prose run; tool calls, thinking and diagrams flush it into a block
 * of their own. Unknown frame types (the retired canvas.* and
 * knowledge.proposal vocabulary among them) skip silently — prose around
 * them stays intact, which is the whole forward-compatibility rule and the
 * reason a client that has never heard of a frame type still draws every
 * one of these turns correctly.
 */
export function buildTurnBlocks(frames: readonly FrameLike[]): TurnBlock[] {
  const blocks: TurnBlock[] = [];
  let prose = '';
  let thinking = '';

  const flushThinking = () => {
    if (thinking.trim().length > 0) {
      blocks.push({ kind: 'thinking', text: thinking.trim() });
    }
    thinking = '';
  };

  const flush = () => {
    if (prose.trim().length > 0) {
      blocks.push({ kind: 'prose', text: prose });
    }
    prose = '';
    flushThinking();
  };

  for (const frame of frames) {
    const type = frameType(frame);
    const data = frameData(frame);
    switch (type) {
      case 'text.delta': {
        const text = data !== null ? asString(data['text']) : null;
        // Thinking and answer never braid into one run: whichever starts
        // closes the other, so a turn that thinks, answers and thinks again
        // keeps all three in the order they happened.
        flushThinking();
        if (text !== null) prose += text;
        break;
      }
      case 'thinking.delta': {
        const text = data !== null ? asString(data['text']) : null;
        if (text === null) break;
        if (prose.trim().length > 0) {
          blocks.push({ kind: 'prose', text: prose });
        }
        prose = '';
        thinking += text;
        break;
      }
      case 'tool.start': {
        const tool = data !== null ? asString(data['tool']) : null;
        const id = data !== null ? asString(data['id']) : null;
        if (tool === null || id === null) break;
        flush();
        blocks.push({ kind: 'activity', toolId: id, tool, summary: null });
        break;
      }
      case 'tool.result': {
        const id = data !== null ? asString(data['id']) : null;
        const summary = data !== null ? asString(data['summary']) : null;
        if (id === null) break;
        const index = blocks.findIndex((b) => b.kind === 'activity' && b.toolId === id);
        if (index === -1) break;
        // Round 4 (owner, on a chip reading "I couldn't read that data shape,
        // continuing"): that tool did not fail. It returned candidates for an
        // ambiguous name and the model asked again with a better one, which is
        // the system working. The server marks such a result `recoverable`,
        // and a recoverable step is stage direction the reader never needed:
        // the chip leaves with it. A result that carries no summary at all
        // says nothing either, so it goes the same way. Anything else renders
        // exactly as it always did, which is what an old unflagged frame is.
        const recoverable = data !== null && data['recoverable'] === true;
        if (recoverable || summary === null) {
          blocks.splice(index, 1);
          break;
        }
        (blocks[index] as Extract<TurnBlock, { kind: 'activity' }>).summary = summary;
        break;
      }
      case 'diagram': {
        const diagram = asDiagramData(data);
        if (diagram === null) break;
        flush();
        blocks.push({ kind: 'diagram', data: diagram });
        break;
      }
      case 'message.done': {
        if (data === null) break;
        const usage = data['usage'];
        const input =
          typeof usage === 'object' && usage !== null
            ? (usage as { input_tokens?: unknown }).input_tokens
            : undefined;
        const output =
          typeof usage === 'object' && usage !== null
            ? (usage as { output_tokens?: unknown }).output_tokens
            : undefined;
        if (typeof input !== 'number' || typeof output !== 'number') break;
        // The cache split rides along when the server sent it. Absent stays
        // absent rather than becoming 0: "no cache fields" and "no cache
        // traffic" price the same, but only one of them is a fact.
        const subset = (key: string): number | undefined => {
          const v =
            typeof usage === 'object' && usage !== null
              ? (usage as Record<string, unknown>)[key]
              : undefined;
          return typeof v === 'number' ? v : undefined;
        };
        const cacheRead = subset('cache_read_tokens');
        const cacheWrite = subset('cache_write_tokens');
        flush();
        blocks.push({
          kind: 'usage',
          usage: {
            input_tokens: input,
            output_tokens: output,
            ...(cacheRead !== undefined ? { cache_read_tokens: cacheRead } : {}),
            ...(cacheWrite !== undefined ? { cache_write_tokens: cacheWrite } : {}),
          },
          stopReason: asString(data['stop_reason']) ?? '',
        });
        break;
      }
      case 'error': {
        const message = data !== null ? asString(data['message']) : null;
        if (message === null) break;
        flush();
        blocks.push({ kind: 'error', message, retryable: data?.['retryable'] === true });
        break;
      }
      default:
        // message.start / canvas.highlight / canvas.clear / retired
        // knowledge.proposal / anything future: nothing to render.
        break;
    }
  }
  flush();
  return foldWorkingNotes(blocks);
}

/**
 * How much prose may open a stretch of work before it counts as the answer
 * rather than a note: a working note is a sentence or two; an answer keeps
 * going.
 */
export const WORKING_NOTE_MAX_CHARS = 240;

/**
 * A short line of prose that is immediately followed by a tool call is the
 * model talking to itself ("Let me pull the exec queue's actual number",
 * "Fixing HTML-escaping mismatches in the labels", "I have plenty for a
 * walkthrough now"), not part of the answer (audit 2026-09-07, a Sonnet
 * session). The voice rules forbid it and the better models honour them;
 * this puts the leak behind the same door as the thinking, on live turns
 * and on replay alike, without touching the persisted frames. Prose that
 * nothing follows, or that runs longer than a note, is the answer.
 */
export function foldWorkingNotes(blocks: TurnBlock[]): TurnBlock[] {
  return blocks.map((block, i) => {
    if (block.kind !== 'prose') return block;
    const next = blocks[i + 1];
    if (next === undefined || next.kind !== 'activity') return block;
    const text = block.text.trim();
    if (text.length === 0 || text.length > WORKING_NOTE_MAX_CHARS) return block;
    if (!WORKING_NOTE_OPENERS.test(text)) return block;
    return { kind: 'thinking', text };
  });
}

/**
 * The voice of a note rather than an answer: the model announcing what it
 * is about to do. Answer prose that a tool call merely interrupts ("Two
 * things happen at once.") does not open this way and stays where it is.
 */
export const WORKING_NOTE_OPENERS =
  /^(?:ok(?:ay)?[,.]?\s+)?(?:let me|let's|i(?:'ll| will| need| have| want| am| should| can|'m| now)|now i|first,? i|fixing|checking|pulling|looking|verifying|digging|grabbing|reading|updating|correct(?:ing)?(?: the)? file path)\b/i;

/** Ordered diagram list for one frame list (malformed/unknown frames skip). */
export function diagramsFromFrames(frames: readonly FrameLike[]): SessionDiagram[] {
  const diagrams: SessionDiagram[] = [];
  for (const frame of frames) {
    if (frameType(frame) !== 'diagram') continue;
    const diagram = asDiagramData(frameData(frame));
    if (diagram !== null) diagrams.push(diagram);
  }
  return diagrams;
}

/** The newest assistant turn: the one the pane lands on. */
export function newestAnswer(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message !== undefined && message.role === 'assistant') return message;
  }
  return null;
}

/**
 * Ordered, de-duplicated diagrams across a session's persisted messages. Each
 * row's own frames: the per-depth variants left with the depth dial
 * (DECISIONS #95), so a turn has exactly one body and one set of pictures.
 */
export function diagramsFromMessages(messages: readonly ChatMessage[]): SessionDiagram[] {
  const seen = new Set<string>();
  const diagrams: SessionDiagram[] = [];
  for (const message of messages) {
    const frames = message.frames;
    if (frames === null) continue;
    for (const diagram of diagramsFromFrames(frames)) {
      if (seen.has(diagram.diagram_id)) continue;
      seen.add(diagram.diagram_id);
      diagrams.push(diagram);
    }
  }
  return diagrams;
}

/**
 * What the NEWEST assistant turn drew — empty when that answer drew nothing.
 *
 * The pane lands on the newest turn's picture, so it needs to know which
 * diagrams belong to that turn rather than to the conversation as a whole. An
 * empty list here is not an error: an answer can be prose alone, and the pane
 * says so instead of blanking (DiagramPane).
 */
export function newestTurnDiagrams(messages: readonly ChatMessage[]): SessionDiagram[] {
  const newest = newestAnswer(messages);
  if (newest === null) return [];
  return newest.frames === null ? [] : diagramsFromFrames(newest.frames);
}

